import { useMemo, useState } from 'react';
import { trans } from '@lingoda/i18n';
import { Alert, Box, FramePageContainer, Stack, Typography } from '@lingoda/ui';
import { Grid } from '@lingoda/ui/layout/GridNext';
import { styled } from '@lingoda/ui/theme';
import { Location } from '@lingoda/monolith-graphql';
import { useGetLifetimeDiscountCoupon } from '@lingoda/subscriptions';
import { PurchaseWarnings } from '../../components';
import SectionWrapper from './CheckoutForm/SectionWrapper';
import CheckoutContentBlocks from './ContentBlocks';
import { CheckoutProduct, CheckoutSubscriptionChange } from './CheckoutFormContainers';
import { parsePurchaseContentBlocks } from './utils';
import { PaperStyled } from './PaperStyled';
import Summary from './Summary';
import CouponSection from './CheckoutForm/CouponSection';
import { CurrencySelect } from './CurrencySelect';
import type { CheckoutPurchase } from './types';

const PageWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        paddingBottom: theme.spacing(13),
    },
}));

interface Props {
    isSubscriptionChange: boolean;
    purchase: CheckoutPurchase;
}

const CheckoutLayout = ({ purchase, isSubscriptionChange }: Props) => {
    const [asyncValidating, setAsyncValidating] = useState(false);

    const purchaseContent = useMemo(
        () => parsePurchaseContentBlocks(purchase.contentBlocks),
        [purchase.contentBlocks],
    );

    const CheckoutForm = isSubscriptionChange ? CheckoutSubscriptionChange : CheckoutProduct;

    const data = useGetLifetimeDiscountCoupon();
    const hasLifetimeDiscount = !data.loading && !!data.couponCode;

    const terms = useMemo(() => {
        if (hasLifetimeDiscount) {
            return [
                ...purchaseContent.terms,
                {
                    icon: null,
                    isStarred: false,
                    label: null,
                    location: Location.CheckoutTermsInformation,
                    table: null,
                    text: trans('lifetime-discount-explanation-terms', {}, 'public-payment'),
                },
            ];
        }

        return purchaseContent.terms;
    }, [hasLifetimeDiscount, purchaseContent.terms]);

    return (
        <PageWrapper>
            <FramePageContainer pt={{ xs: 2, sm: 3 }} pb={{ xs: 2, sm: 3 }}>
                <Grid container spacing={{ xs: 2, md: 3, lg: 5 }}>
                    <Grid xs={12} md={7}>
                        <Stack spacing={2}>
                            {purchaseContent.warnings.length > 0 && (
                                <PurchaseWarnings warnings={purchaseContent.warnings} />
                            )}
                            <PaperStyled variant="outlined">
                                <Stack spacing={{ xs: 3, sm: 4 }}>
                                    <SectionWrapper>
                                        <Typography variant="h1" color="textPrimary">
                                            {trans('complete-your-purchase', {}, 'public-payment')}
                                        </Typography>
                                    </SectionWrapper>
                                    <Box>
                                        {purchaseContent.informationBanners.length > 0 && (
                                            <SectionWrapper>
                                                <CheckoutContentBlocks
                                                    informationContentBlocks={
                                                        purchaseContent.informationBanners
                                                    }
                                                />
                                            </SectionWrapper>
                                        )}
                                        {hasLifetimeDiscount && (
                                            <Box mt={1}>
                                                <SectionWrapper>
                                                    <Alert
                                                        severity="success"
                                                        size="compact"
                                                        variant="outlined"
                                                        title={trans(
                                                            'your-lifetime-discount-has-been-automatically-applied',
                                                            {},
                                                            'public-payment',
                                                        )}
                                                    />
                                                </SectionWrapper>
                                            </Box>
                                        )}
                                    </Box>
                                    <CheckoutForm
                                        submitLabel={purchaseContent.submitLabel}
                                        purchase={purchase}
                                        asyncValidating={asyncValidating}
                                        testChargeLabel={purchaseContent.testChargeLabel}
                                        terms={terms}
                                    />
                                </Stack>
                            </PaperStyled>
                        </Stack>
                    </Grid>
                    <Grid xs={12} md={5}>
                        <Summary
                            purchase={purchase}
                            currencySection={<CurrencySelect purchase={purchase} />}
                            couponSection={
                                <CouponSection
                                    purchaseId={purchase.id}
                                    appliedCode={purchase.coupons[0]?.code}
                                    setAsyncValidating={setAsyncValidating}
                                    keepInputAfterApply
                                />
                            }
                        />
                    </Grid>
                </Grid>
            </FramePageContainer>
        </PageWrapper>
    );
};

export default CheckoutLayout;
